
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function ApplyPage() {
  const [step, setStep] = useState(1);
  const [country, setCountry] = useState('');
  const [creditType, setCreditType] = useState('');
  const [formData, setFormData] = useState({
    // Datos personales
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    birthDate: '',
    nationality: '',
    idNumber: '',
    // Dirección
    address: '',
    city: '',
    state: '',
    postalCode: '',
    // Datos laborales
    employmentStatus: '',
    employer: '',
    position: '',
    monthlyIncome: '',
    yearsEmployed: '',
    // Datos del crédito
    requestedAmount: '',
    requestedTerm: '',
    purpose: '',
    // Referencias
    reference1Name: '',
    reference1Phone: '',
    reference2Name: '',
    reference2Phone: '',
    // Aceptación
    acceptTerms: false,
    acceptPrivacy: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('country', country);
      formDataToSend.append('creditType', creditType);
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d5ki1ok8iuo4ujd55u80', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '', lastName: '', email: '', phone: '', birthDate: '', nationality: '', idNumber: '',
          address: '', city: '', state: '', postalCode: '',
          employmentStatus: '', employer: '', position: '', monthlyIncome: '', yearsEmployed: '',
          requestedAmount: '', requestedTerm: '', purpose: '',
          reference1Name: '', reference1Phone: '', reference2Name: '', reference2Phone: '',
          acceptTerms: false, acceptPrivacy: false
        });
        setStep(1);
        setCountry('');
        setCreditType('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedToStep2 = country && creditType;
  const canProceedToStep3 = formData.firstName && formData.lastName && formData.email && formData.phone && formData.birthDate && formData.idNumber;
  const canProceedToStep4 = formData.address && formData.city && formData.state;
  const canProceedToStep5 = formData.employmentStatus && formData.monthlyIncome;
  const canSubmit = formData.requestedAmount && formData.requestedTerm && formData.acceptTerms && formData.acceptPrivacy;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <section className="pt-32 pb-24 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-800 mb-6">
              Solicitar Crédito
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Completa tu solicitud en línea de forma segura y confidencial
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4">
              {[1, 2, 3, 4, 5].map((s) => (
                <div key={s} className="flex items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${step >= s ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                    {s}
                  </div>
                  {s < 5 && <div className={`flex-1 h-1 mx-2 ${step > s ? 'bg-emerald-600' : 'bg-slate-200'}`}></div>}
                </div>
              ))}
            </div>
            <div className="flex justify-between text-xs text-slate-600">
              <span>Tipo</span>
              <span>Personales</span>
              <span>Dirección</span>
              <span>Laborales</span>
              <span>Crédito</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} data-readdy-form className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            {/* Step 1: Tipo de Crédito */}
            {step === 1 && (
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Selecciona tu País y Tipo de Crédito</h2>
                
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-slate-800 mb-3">País *</label>
                  <select 
                    name="country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                    required
                  >
                    <option value="">Selecciona un país</option>
                    <option value="CL">🇨🇱 Chile</option>
                    <option value="PE">🇵🇪 Perú</option>
                    <option value="MX">🇲🇽 México</option>
                  </select>
                </div>

                <div className="mb-8">
                  <label className="block text-sm font-semibold text-slate-800 mb-3">Tipo de Crédito *</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { value: 'personal', icon: 'ri-user-fill', title: 'Crédito Personal', desc: 'Hasta $50,000 USD' },
                      { value: 'micro', icon: 'ri-flashlight-fill', title: 'Microcrédito', desc: 'Hasta $5,000 USD' },
                      { value: 'business', icon: 'ri-building-fill', title: 'Empresarial', desc: 'Hasta $500,000 USD' },
                      { value: 'mortgage', icon: 'ri-home-fill', title: 'Hipotecario', desc: 'Hasta 80% del valor' }
                    ].map((type) => (
                      <div 
                        key={type.value}
                        onClick={() => setCreditType(type.value)}
                        className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${creditType === type.value ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 hover:border-emerald-300'}`}
                      >
                        <i className={`${type.icon} text-3xl mb-3 ${creditType === type.value ? 'text-emerald-600' : 'text-slate-400'}`}></i>
                        <h3 className="font-bold text-slate-800 mb-1">{type.title}</h3>
                        <p className="text-xs text-slate-600">{type.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <button 
                  type="button"
                  onClick={() => setStep(2)}
                  disabled={!canProceedToStep2}
                  className="w-full py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-all disabled:bg-slate-300 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Continuar
                </button>
              </div>
            )}

            {/* Step 2: Datos Personales */}
            {step === 2 && (
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Datos Personales</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Nombre(s) *</label>
                    <input 
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Apellido(s) *</label>
                    <input 
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Correo Electrónico *</label>
                    <input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Teléfono *</label>
                    <input 
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Fecha de Nacimiento *</label>
                    <input 
                      type="date"
                      name="birthDate"
                      value={formData.birthDate}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Número de Identificación *</label>
                    <input 
                      type="text"
                      name="idNumber"
                      value={formData.idNumber}
                      onChange={handleInputChange}
                      placeholder="RUT / DNI / RFC"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <button 
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 py-4 bg-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-300 transition-all whitespace-nowrap"
                  >
                    Atrás
                  </button>
                  <button 
                    type="button"
                    onClick={() => setStep(3)}
                    disabled={!canProceedToStep3}
                    className="flex-1 py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-all disabled:bg-slate-300 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    Continuar
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Dirección */}
            {step === 3 && (
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Dirección</h2>
                
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-slate-800 mb-2">Dirección Completa *</label>
                  <input 
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Ciudad *</label>
                    <input 
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Estado/Región *</label>
                    <input 
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Código Postal</label>
                    <input 
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <button 
                    type="button"
                    onClick={() => setStep(2)}
                    className="flex-1 py-4 bg-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-300 transition-all whitespace-nowrap"
                  >
                    Atrás
                  </button>
                  <button 
                    type="button"
                    onClick={() => setStep(4)}
                    disabled={!canProceedToStep4}
                    className="flex-1 py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-all disabled:bg-slate-300 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    Continuar
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Datos Laborales */}
            {step === 4 && (
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Información Laboral y Financiera</h2>
                
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-slate-800 mb-2">Situación Laboral *</label>
                  <select 
                    name="employmentStatus"
                    value={formData.employmentStatus}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                    required
                  >
                    <option value="">Selecciona una opción</option>
                    <option value="employed">Empleado</option>
                    <option value="self-employed">Independiente</option>
                    <option value="business-owner">Empresario</option>
                    <option value="retired">Jubilado</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Empresa/Empleador</label>
                    <input 
                      type="text"
                      name="employer"
                      value={formData.employer}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Cargo/Posición</label>
                    <input 
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Ingreso Mensual (USD) *</label>
                    <input 
                      type="number"
                      name="monthlyIncome"
                      value={formData.monthlyIncome}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Años en el Empleo</label>
                    <input 
                      type="number"
                      name="yearsEmployed"
                      value={formData.yearsEmployed}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <button 
                    type="button"
                    onClick={() => setStep(3)}
                    className="flex-1 py-4 bg-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-300 transition-all whitespace-nowrap"
                  >
                    Atrás
                  </button>
                  <button 
                    type="button"
                    onClick={() => setStep(5)}
                    disabled={!canProceedToStep5}
                    className="flex-1 py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-all disabled:bg-slate-300 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    Continuar
                  </button>
                </div>
              </div>
            )}

            {/* Step 5: Detalles del Crédito */}
            {step === 5 && (
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Detalles del Crédito Solicitado</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Monto Solicitado (USD) *</label>
                    <input 
                      type="number"
                      name="requestedAmount"
                      value={formData.requestedAmount}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Plazo Deseado (meses) *</label>
                    <input 
                      type="number"
                      name="requestedTerm"
                      value={formData.requestedTerm}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-slate-800 mb-2">Propósito del Crédito</label>
                  <textarea 
                    name="purpose"
                    value={formData.purpose}
                    onChange={handleInputChange}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm resize-none"
                    placeholder="Describe brevemente el uso que darás al crédito (máximo 500 caracteres)"
                  ></textarea>
                  <p className="text-xs text-slate-500 mt-1">{formData.purpose.length}/500 caracteres</p>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-bold text-slate-800 mb-4">Referencias Personales</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-800 mb-2">Nombre Referencia 1</label>
                      <input 
                        type="text"
                        name="reference1Name"
                        value={formData.reference1Name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-800 mb-2">Teléfono Referencia 1</label>
                      <input 
                        type="tel"
                        name="reference1Phone"
                        value={formData.reference1Phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-800 mb-2">Nombre Referencia 2</label>
                      <input 
                        type="text"
                        name="reference2Name"
                        value={formData.reference2Name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-800 mb-2">Teléfono Referencia 2</label>
                      <input 
                        type="tel"
                        name="reference2Phone"
                        value={formData.reference2Phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-6 mb-6">
                  <div className="flex items-start space-x-3 mb-4">
                    <input 
                      type="checkbox"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={handleInputChange}
                      className="mt-1 w-5 h-5 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500 cursor-pointer"
                      required
                    />
                    <label className="text-sm text-slate-700">
                      Acepto los <Link to="/terminos-condiciones" className="text-emerald-600 hover:underline" target="_blank">Términos y Condiciones</Link> de Novacréditos *
                    </label>
                  </div>
                  <div className="flex items-start space-x-3">
                    <input 
                      type="checkbox"
                      name="acceptPrivacy"
                      checked={formData.acceptPrivacy}
                      onChange={handleInputChange}
                      className="mt-1 w-5 h-5 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500 cursor-pointer"
                      required
                    />
                    <label className="text-sm text-slate-700">
                      He leído y acepto el <Link to="/aviso-privacidad" className="text-emerald-600 hover:underline" target="_blank">Aviso de Privacidad</Link> y autorizo el tratamiento de mis datos personales *
                    </label>
                  </div>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-6">
                  <div className="flex items-start space-x-3">
                    <i className="ri-shield-check-fill text-emerald-600 text-xl mt-0.5"></i>
                    <div>
                      <p className="text-sm text-emerald-900 font-semibold mb-2">Información Segura y Confidencial</p>
                      <p className="text-xs text-emerald-800 leading-relaxed">
                        Todos tus datos están protegidos con cifrado SSL de nivel bancario. Esta solicitud no constituye una aprobación automática y está sujeta a evaluación crediticia según nuestras políticas.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button 
                    type="button"
                    onClick={() => setStep(4)}
                    className="flex-1 py-4 bg-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-300 transition-all whitespace-nowrap"
                  >
                    Atrás
                  </button>
                  <button 
                    type="submit"
                    disabled={!canSubmit || isSubmitting}
                    className="flex-1 py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-all disabled:bg-slate-300 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
                  </button>
                </div>
              </div>
            )}

            {/* Success/Error Messages */}
            {submitStatus === 'success' && (
              <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                <div className="flex items-start space-x-3">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-2xl"></i>
                  <div>
                    <p className="text-emerald-900 font-semibold mb-2">¡Solicitud Enviada Exitosamente!</p>
                    <p className="text-sm text-emerald-800 leading-relaxed">
                      Hemos recibido tu solicitud de crédito. Nuestro equipo la revisará y te contactaremos en las próximas 24-48 horas para continuar con el proceso de evaluación.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6">
                <div className="flex items-start space-x-3">
                  <i className="ri-error-warning-fill text-red-600 text-2xl"></i>
                  <div>
                    <p className="text-red-900 font-semibold mb-2">Error al Enviar la Solicitud</p>
                    <p className="text-sm text-red-800 leading-relaxed">
                      Hubo un problema al procesar tu solicitud. Por favor, intenta nuevamente o contáctanos directamente.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-slate-600 mb-4">
              ¿Necesitas ayuda con tu solicitud?
            </p>
            <Link to="/contacto" className="text-emerald-600 font-semibold hover:text-emerald-700 transition-colors text-sm">
              Contactar a un Asesor →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
